/* eslint-disable max-lines */
import { QueryClient } from "@tanstack/react-query";
import { IntlShape } from "react-intl";
import moment from "moment";

import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { DeliveryStopPod } from "api/response";
import getLabel from "helpers/getLabel";
import { capitalizeFirstLetter, generateUUID } from "helpers/commonHelpers";
import { hasLink } from "helpers/urlHelpers";
import { DateTimeFormat, getDifferenceInDates } from "helpers/dateTimeHelper";
import {
  TrackingItemProps,
  TrackingItemWithHistoryProps,
  TrackingItemWithoutHistoryProps,
} from "./components/TrackingItem/TrackingItem";
import { TrackingHistoryItemProps } from "./components/TrackingHistoryItem/TrackingHistoryItem";
import { GroupedTrackingHistoryItem } from "./components/GroupedTrackingHistory/GroupedTrackingHistory";
import { TrackingExceptionProps } from "./components/TrackingException/TrackingException";
import { TrackingMessageType } from "./components/TrackingItem/TrackingItemTypes";
import { AssociatedStopScheduleWindow, resolveAssociatedStopLabel } from "./formatAssociatedStopLabel";

type AssociatedStopLabelSources = {
  deliveryStops?: DeliveryStopPod[];
  scheduleStops?: AssociatedStopScheduleWindow[];
};

const PORTCAST_DELAY_REASONS: any = {
  RLV: "Vessel Rollover",
  LTD: "Late Departure",
  PRC: "Port Rotation Changes",
} as const;

/**
 * STEP 0 - Generate a list of tracking items to be used in the tracking accordion.
 * This function formats the data into the correct format for the TrackingItem component.
 * @param trackingUpdates - The list of updates for the shipment.
 * @param trackingNumbers - The list of tracking numbers for the shipment.
 * @returns an array of tracking items.
 */
const generateTrackingItems = (
  trackingUpdates: any,
  trackingNumbers: any,
  trackingNumberOptions: any,
  intl: IntlShape,
  company_shipper_settings?: any,
  stopLabelSources?: AssociatedStopLabelSources,
): TrackingItemProps[] => {
  let trackingItems: TrackingItemProps[] = [];

  /**
   * Create the tracking items for the shipment.
   * @example trackingItems = [{ id: 1, trackingLabel: "Container number", trackingNumber: "123456", type: "container_no", message: "No updates available" }]
   */
  trackingItems = createTrackingItems(
    trackingNumbers,
    trackingNumberOptions,
    intl,
    company_shipper_settings,
    stopLabelSources,
  );

  // Create the history for each tracking item and get the latest update info, if trackingUpdates is available
  return trackingItems.map((trackingItem) => {
    if (trackingUpdates) {
      // Filter relevant updates for this tracking number
      const updatesForThisTrackingNumber = getUpdatesByTrackingNumber(trackingUpdates, trackingItem.trackingNumber);

      const history = createHistory(trackingItem, updatesForThisTrackingNumber, intl);
      const latestUpdate = getLatestUpdateInfo(history, trackingItem.isGroupedHistory || false);
      const exceptionProps = getExceptionPropsForTrackingItem(updatesForThisTrackingNumber, intl);

      const messageForContainerBOLWarning =
        trackingItem.type === "container_no" &&
        company_shipper_settings?.ocean_tracking_enabled &&
        updatesForThisTrackingNumber?.length > 0 &&
        updatesForThisTrackingNumber[updatesForThisTrackingNumber.length - 1]?.additional_info?.status_info
          ?.metadata?.[0] === "BILL_OF_LADING_BOOKMARK_WARNING"
          ? intl.formatMessage({
              id: "tracking.error.BOLBookmarkWarning",
              defaultMessage:
                "Unable to track your shipment because the container or BL is missing, incorrect, or not linked. Please check the numbers or try without a BL.",
            })
          : null;

      // If the tracking number is a MAWB and all updates are estimated, show a message
      const messageForMAWBEstimatedEvents =
        trackingItem.type === "mawb" &&
        company_shipper_settings?.air_tracking_enabled &&
        updatesForThisTrackingNumber?.length > 0 &&
        updatesForThisTrackingNumber?.every(
          (item: any) => item?.milestone_details?.estimated_time && !item?.milestone_details?.actual_time,
        )
          ? intl.formatMessage({
              id: "tracking.error.MAWBEstimatedWarning",
              defaultMessage: "Tracking events and timestamps are still updating. Please check back later.",
            })
          : null;

      if (history.length > 0) {
        return {
          ...trackingItem,
          ...latestUpdate,
          message:
            messageForContainerBOLWarning || messageForMAWBEstimatedEvents || latestUpdate.message || trackingItem.message,
          uuid: generateUUID(),
          history,
          isExpanded: false,
          isGroupedHistory: trackingItem.isGroupedHistory || false,
          exceptionProps,
        } as TrackingItemWithHistoryProps;
      }
    }

    // If no trackingUpdates or no history, return the item without history
    return {
      ...trackingItem,
      uuid: generateUUID(),
    } as TrackingItemWithoutHistoryProps;
  });
};
/**
 * STEP 1 - Create the base tracking items for the shipment.
 * @param trackingNumbers - The list of tracking numbers for the shipment.
 * @param trackingNumberOptions - The list of tracking number options.
 * @returns an array of TrackingItemProps.
 */
const createTrackingItems = (
  trackingNumbers: any,
  trackingNumberOptions: any,
  intl: IntlShape,
  company_shipper_settings: any,
  stopLabelSources?: AssociatedStopLabelSources,
): TrackingItemProps[] =>
  trackingNumbers.map((trackingNumber: any) => {
    const { bol, scac, tracking_number, type, id, created_at, truck_tracking_info, stop } = trackingNumber;
    const { tracking_url, status } = truck_tracking_info ?? {};
    const trackingLabel = trackingNumberOptions.find((option: any) => option.value === type)?.label || type;

    const unsupportedLiveTrackingTypes = ["rail_car"];

    let message: TrackingMessageType = null;

    // Set the message for unsupported live tracking types
    if (unsupportedLiveTrackingTypes.includes(type)) {
      message = intl.formatMessage({
        id: "tracking.doNotSupportTrackingType",
        defaultMessage: "We currently do not support live tracking of this reference type.",
      });
    } else if (type === "container_no" && !company_shipper_settings?.ocean_tracking_enabled) {
      message = intl.formatMessage({
        id: "tracking.error.enableContainerTracking",
        defaultMessage:
          "We could not track your shipment because your company did not enable container tracking in Cargobase.",
      });
    } else if (type === "mawb" && !company_shipper_settings?.air_tracking_enabled) {
      message = intl.formatMessage({
        id: "tracking.error.enableMAWBTracking",
        defaultMessage: "We could not track your shipment because your company did not enable MAWB tracking in Cargobase.",
      });
    } else if (type === "truck" && tracking_url && company_shipper_settings?.truck_tracking_enabled) {
      message = {
        type: "trackingLinkForDriver",
        props: {
          link: tracking_url,
          status,
        },
      };
    }

    // Set the message based on the time since the tracking number was created
    const associatedStopLabel = resolveAssociatedStopLabel(
      stop,
      stopLabelSources?.deliveryStops,
      stopLabelSources?.scheduleStops,
    );

    const utcNow = moment.utc().format("YYYY-MM-DD HH:mm:ss");
    const isLessThan24HoursSinceCreated = moment(utcNow).diff(created_at, "hours") < 24;
    if (!message) {
      message = isLessThan24HoursSinceCreated
        ? intl.formatMessage({
            id: "tracking.retrievingShipmentStatus",
            defaultMessage: "We are retrieving the status of your shipment. Please check back later.",
          })
        : intl.formatMessage({
            id: "tracking.couldNotTrackShipment",
            defaultMessage:
              "We could not track your shipment. Check if you’ve entered the correct tracking number and try again.",
          });
    }

    return {
      id,
      trackingLabel,
      trackingNumber: removeSlashes(tracking_number),
      type,
      message,
      ...(scac && { scacCode: scac }),
      ...(bol && { supportingText: bol }),
      ...(associatedStopLabel && { associatedStopLabel }),
    };
  });

/**
 * STEP 2 - Create a history for the tracking item.
 * Groups them if any of the updates have a location.
 * @param trackingItem - the tracking item to create history for
 * @param trackingUpdates - all updates for the shipments
 */
const createHistory = (
  trackingItem: TrackingItemProps,
  updatesForThisTrackingNumber: any,
  intl: IntlShape,
): TrackingHistoryItemProps[] | GroupedTrackingHistoryItem[] => {
  // If there are no updates, return an empty array
  if (!updatesForThisTrackingNumber?.length) return [];

  // Create history items and determine if they should be grouped
  const { trackingHistoryItems, isGroupedHistory } = createHistoryItems(updatesForThisTrackingNumber, intl);
  trackingItem.isGroupedHistory = isGroupedHistory;

  // If the history is not grouped, return the history items
  if (!isGroupedHistory) return trackingHistoryItems;

  // Otherwise, group the history items by location
  return trackingHistoryItems.reduce((groups: any[], item: any) => {
    let groupName = item.location || "System update";
    const hasException = Boolean(item.exceptionProps);

    if (groupName.toLowerCase() === "not available") groupName = "System update"; // If location is "Not available", group as "System update"

    const lastGroup = groups[groups.length - 1];
    if (lastGroup && `${lastGroup.groupName} ${lastGroup.groupSubtitle}` === `${groupName} ${item.locationGroupSubtitle}`) {
      if (hasException && !lastGroup.hasException) lastGroup.hasException = true;
      lastGroup.history.push(item);
    } else {
      // Get group information
      const groupSubtitle = item.locationGroupSubtitle || "";
      const badgeText = item.locationGroupBadgeText || "";
      groups.push({
        groupName,
        history: [item],
        hasException,
        ...(groupSubtitle && { groupSubtitle }),
        ...(badgeText && { badgeText }),
      });
    }

    return groups;
  }, []);
};

/**
 * (PART OF STEP 2) - Create the individual history items.
 */
const createHistoryItems = (
  updates: any,
  intl: IntlShape,
): { trackingHistoryItems: TrackingHistoryItemProps[]; isGroupedHistory: boolean } => {
  let isGroupedHistory = false;
  // CREATE HISTORY FOR PORTCAST
  if (updates.every((update: any) => !!update?.additional_info?.container_metadata?.cntr_no)) {
    const latestUpdate = updates.at(-1);
    const containerEventList = updates.length > 0 ? updates[updates.length - 1].additional_info.container_event_list : [];
    const exceptionData = getPortcastExceptionDataForTrackingHistoryItem(latestUpdate);
    const trackingHistoryItems = containerEventList.map((containerEvent: any) => {
      const {
        event_raw,
        location_raw,
        terminal_details,
        mode_of_transport,
        event_time_estimated,
        event_time,
        vessel_imo,
        vessel_name,
        location_type_name,
        port_code,
        event_type_code,
      } = containerEvent;
      const { terminal_name, terminal_code } = terminal_details;

      const modeOfTransportationName = vessel_imo ? `${vessel_name} (${vessel_imo})` : vessel_name;
      const terminal = terminal_code ? `${terminal_name || ""} (${terminal_code})` : terminal_name || "";
      let exceptionProps: TrackingExceptionProps | undefined;

      if (exceptionData && exceptionData[`${port_code}_${event_type_code}`]) {
        const { exceptionReason, previousEstimatedTime, updatedEstimatedTime } =
          exceptionData[`${port_code}_${event_type_code}`];

        let delayReason = "";
        let exceptionLabel = "Exception";
        if (exceptionReason.length > 0) {
          delayReason = exceptionReason.map((reason: string) => PORTCAST_DELAY_REASONS[reason]).join("; ");
        }
        if (updatedEstimatedTime && previousEstimatedTime) {
          const differenceInDays = getDifferenceInDates(updatedEstimatedTime, previousEstimatedTime, "days");
          if (differenceInDays > 0) {
            exceptionLabel = `Late by ${differenceInDays} day${differenceInDays > 1 ? "s" : ""}`;
          }
        }
        exceptionProps = {
          exceptionLabel,
          labelAdditionalInfo: delayReason,
          exceptionDetails: [
            {
              title: intl.formatMessage({
                id: "tracking.exception.delayReason",
                defaultMessage: "Delay reason",
              }),
              description: delayReason,
            },
            {
              title: intl.formatMessage({
                id: "tracking.exception.previousEstimatedTime",
                defaultMessage: "Previous estimated time",
              }),
              description: moment(previousEstimatedTime).format(DateTimeFormat.fullDateTimeFormat),
            },
            {
              title: intl.formatMessage({
                id: "tracking.exception.updatedEstimatedTime",
                defaultMessage: "Updated estimated time",
              }),
              description: moment(updatedEstimatedTime).format(DateTimeFormat.fullDateTimeFormat),
            },
          ],
        };
      }

      // Determine if history should be grouped
      if (location_raw) isGroupedHistory = true;

      return {
        date: event_time || event_time_estimated,
        isEstimatedDate: !event_time && event_time_estimated,
        message: event_raw,
        ...(mode_of_transport && { modeOfTransportation: mode_of_transport }),
        ...(modeOfTransportationName && { modeOfTransportationName }),
        ...(location_raw && { location: location_raw }),
        ...(terminal && { terminal }),
        ...(location_type_name && { locationGroupBadgeText: location_type_name }),
        ...(port_code && { locationGroupSubtitle: port_code }),
        ...(exceptionProps && { exceptionProps }),
      };
    });

    return { trackingHistoryItems, isGroupedHistory };
  }

  const isPortcastAir = updates.every((update: any) => Boolean(update?.flight_information));
  if (isPortcastAir) {
    /**
     * Order air milestones by the journey's route sequence (BOM→GYD→LGG→FRA), then by their
     * effective time within each location. The top-level `timestamp` is unreliable for ordering:
     * estimated events carry the data-ingest time (not their real milestone time), and source
     * timestamps across locations can be internally inconsistent. Grouping is adjacency-based, so
     * ordering by route sequence keeps each location (e.g. the origin) as a single contiguous group.
     *
     * Only applied when route legs are available. Air sources without route/location data keep
     * their incoming (timestamp-sorted) order untouched, so no other tracking source is affected.
     */
    const routeOrder: string[] = [];
    (updates[0]?.routes ?? []).forEach((route: any) => {
      [route?.from?.iata_code, route?.to?.iata_code].forEach((code: string) => {
        if (code && !routeOrder.includes(code)) routeOrder.push(code);
      });
    });
    const getEffectiveTime = (update: any) =>
      update?.milestone_details?.actual_time ||
      update?.milestone_details?.estimated_time ||
      update?.timestamp ||
      update?.created;
    const getRouteRank = (update: any) => {
      const index = routeOrder.indexOf(update?.location?.iata_code);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
    };
    const orderedUpdates =
      routeOrder.length > 0
        ? [...updates].sort((a: any, b: any) => {
            const rankDiff = getRouteRank(a) - getRouteRank(b);
            if (rankDiff !== 0) return rankDiff;
            return new Date(getEffectiveTime(a)).getTime() - new Date(getEffectiveTime(b)).getTime();
          })
        : updates;

    const trackingHistoryItems = orderedUpdates.map((update: any) => {
      const {
        created,
        timestamp,
        milestone_details,
        additional_info,
        event_weight,
        weight,
        event_piece,
        piece,
        flight_information,
        mode_of_transport,
        from,
        to,
      } = update;
      const { state, country } = update?.location ?? {};
      const location = stringifyStateAndCountry(state, country);
      const isOrigin = update?.location?.iata_code === from?.iata_code;
      const isDestination = update?.location?.iata_code === to?.iata_code;

      return {
        // Will never be timestamp or created, but we need to keep it for fail safe
        date: milestone_details?.actual_time || milestone_details?.estimated_time || timestamp || created,
        isEstimatedDate: !milestone_details?.actual_time,
        location,
        message: stringifyPortcastAirEventType(milestone_details?.event_type) ?? additional_info?.description,
        event_weight: event_weight ? Number(event_weight) : null,
        weight: weight ? Number(weight) : null,
        event_piece: event_piece ? Number(event_piece) : null,
        piece: piece ? Number(piece) : null,
        airline: flight_information?.airline,
        locationGroupBadgeText: isOrigin
          ? intl.formatMessage({
              id: "tracking.origin",
              defaultMessage: "Origin",
            })
          : isDestination
          ? intl.formatMessage({
              id: "tracking.destination",
              defaultMessage: "Destination",
            })
          : null,
        locationGroupSubtitle: `${update?.location?.location_text} (${update?.location?.iata_code})`,

        // We put it under modeOfTransportationName, because in TrackingHistoryItem, we render modeOfTransportationName next to the icon
        modeOfTransportationName: flight_information?.flight_number || null,
        ...(mode_of_transport && { modeOfTransportation: mode_of_transport }),
      };
    });

    return {
      trackingHistoryItems: [...trackingHistoryItems, ...createExtraPortcastAirUpdates(orderedUpdates, intl)],
      isGroupedHistory: true,
    };
  }

  // CREATE HISTORY FOR OTHER UPDATES
  const trackingHistoryItems = updates.map((update: any) => {
    const { created, timestamp, message, data } = update;
    const { state, country } = update?.location ?? {};
    let alternateLocation;
    if (state || country) alternateLocation = stringifyStateAndCountry(state, country);

    // Determine location
    let location = data?.find((item: any) => item.key === "location")?.value || alternateLocation || "";
    if (!location && message?.includes("Location:")) {
      location = message.split("Location:")[1]?.trim() || ""; // If location is not in data, extract it from the message
    }

    // Get message
    const status = data?.find((item: any) => item.key === "status")?.value || "";
    const messageWithoutLocation = message?.split("Location:")[0].trim();
    const formattedMessage = capitalizeFirstLetter(status || messageWithoutLocation); // If status is not in data, use the message

    // Get mode of transportation
    const modeOfTransportation = data?.find((item: any) => item.key === "mode_of_transport")?.value || null;
    let modeOfTransportationName = data?.find((item: any) => item.key === "vessel_name")?.value || null;
    const vesselImo = data?.find((item: any) => item.key === "vessel_imo_number")?.value || null;
    if (vesselImo) modeOfTransportationName += ` (${vesselImo})`;

    // Determine if history should be grouped
    if (location && location !== "Not available") isGroupedHistory = true;

    let exceptionProps: TrackingExceptionProps | null = null;
    if (update?.tag === "Exception") {
      const exceptionDescription = update?.additional_info?.exceptionDescription;
      if (exceptionDescription) {
        exceptionProps = {
          exceptionLabel: "Exception",
          labelAdditionalInfo: exceptionDescription,
        };
      } else {
        exceptionProps = {
          exceptionLabel: update?.subtag_display,
          ...(update?.subtag && {
            exceptionDetails: [
              {
                description: stringifyExceptionSubtag(update?.subtag),
              },
            ],
          }),
        };
      }
    }

    return {
      date: timestamp || created,
      message: formattedMessage,
      ...(modeOfTransportation && { modeOfTransportation }),
      ...(modeOfTransportationName && { modeOfTransportationName }),
      ...(location && { location }),
      hasException: Boolean(exceptionProps),
      ...(exceptionProps && { exceptionProps }),
      ...(update?.location?.location_text && location && { locationGroupSubtitle: update.location.location_text }),
    };
  });

  return { trackingHistoryItems, isGroupedHistory };
};

/**
 * STEP 3 - Get the latest update info for the tracking item.
 * @param history - The history of the tracking item.
 * @param isGroupedHistory - Whether the history is grouped.
 * @returns the latest update info.
 */
const getLatestUpdateInfo = (history: any, isGroupedHistory: boolean) => {
  let latestUpdate: any = {};

  const updateArray = isGroupedHistory
    ? history.reduce((acc: TrackingHistoryItemProps[], group: any) => [...acc, ...group.history], [])
    : history;

  if (updateArray.length) {
    const pastUpdates = updateArray.filter(
      (update: TrackingHistoryItemProps) => new Date(update.date) < new Date() && !update?.isEstimatedDate,
    );
    if (pastUpdates.length) {
      // Get the last update in the filtered array
      const lastUpdate = pastUpdates[pastUpdates.length - 1];
      latestUpdate = {
        date: lastUpdate.date,
        message: lastUpdate.message,
        location: lastUpdate.location === "Not available" ? null : lastUpdate.location,
      };
    }
  }

  return latestUpdate;
};

/**
 * Remove slashes from a string
 * @param {*} text - The text to remove slashes from
 * @returns {string} - The text with slashes removed
 */
function removeSlashes(text: string) {
  if (text && text.includes("||")) return text.split("||").join("");
  return text;
}

/**
 * Get the tracking number options based on the tracking labels returned by the API.
 * @param data - The tracking number response data.
 * @param paymentLocation - The payment location of the shipment.
 * @returns a list of tracking number options.
 */
const getTrackingNumberOptions = (
  data: any,
  paymentLocation: string,
  options?: { excludeBookingReference?: boolean },
) => {
  // Set the available tracking number options based on the tracking labels returned by the API
  const trackingLabels = data.tracking_settings.tracking_labels;
  const trackingNumberOptions = Object.entries(trackingLabels).map(([key, value]) => ({ label: value, value: key }));

  // If "Other tracking" is enabled, update the label to use the custom label of the payment location
  const otherTrackingIndex = trackingNumberOptions.findIndex((option: any) => option.label === "Other tracking");
  if (otherTrackingIndex !== -1) {
    trackingNumberOptions[otherTrackingIndex].label = getLabel("Other tracking", "capitalize-first", paymentLocation, true);
  }

  // If "booking reference" is enabled, add it to the tracking number options
  const isBookingReferenceEnabled = data.tracking_settings.booking_reference_enabled !== "off";
  if (isBookingReferenceEnabled && !options?.excludeBookingReference) {
    trackingNumberOptions.push({
      label: getLabel("Booking reference", "capitalize", undefined, true),
      value: "bookingReference",
    });
  }

  return trackingNumberOptions;
};

/**
 * Refreshes the tracking data.
 * If the type is container_no, we need to start polling to get the references because there is a delay before the data is available.
 * @param type - The type of tracking number.
 * @param queryClient - The query client.
 */
const refreshTrackingData = (type: string, queryClient: QueryClient, id: string, startPolling?: Function) => {
  if (type && type === "container_no" && startPolling) {
    startPolling({
      fn: () => queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentTrackingReferences(id) }),
      delay: 3000,
      maxPollCount: 2,
      callFirstTimeImmediately: true,
      onComplete: () => {},
    });
  }

  queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentDetails(id) });
  queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentGeoDataDetails(id) });
  queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentTrackingReferences(id) });
};

/**
 * Sorts the data by timestamp.
 * @param data - An array of data items to sort by timestamp.
 * @returns the sorted data.
 */
function sortByTimestamp(data: { updates: { timestamp: string; created: string }[] }) {
  if (!data?.updates) return data;
  data.updates.sort((a, b) => {
    const dateA = new Date(a.timestamp || a.created);
    const dateB = new Date(b.timestamp || b.created);
    return dateA.getTime() - dateB.getTime();
  });

  return data;
}

/**
 * Get all the updates for a specific tracking number.
 * @param trackingUpdates - all updates for the shipments
 * @param trackingNumber - the tracking number to get updates for
 */
function getUpdatesByTrackingNumber(trackingUpdates: any, trackingNumber: string) {
  return trackingUpdates?.filter((update: any) => {
    // PORTCAST CONTAINER UPDATE
    const isPortcastContainer =
      !!update?.additional_info?.container_metadata?.cntr_no &&
      update.additional_info.container_metadata.cntr_no.toUpperCase() === trackingNumber.toUpperCase();

    if (isPortcastContainer) return true;

    // OTHER UPDATES
    const isMatchingTrackingNumber = removeSlashes(update.group) === trackingNumber || update.group === trackingNumber;
    const isSystemUpdate =
      update.author_name === "System" &&
      !["Cargobase", "Cargobase System"].includes(update?.company?.company_name) &&
      update.type !== "delivered" &&
      !hasLink(update.message);
    const isAllowedUpdateType = ["webhook", "schedule_change", "service_change", "auto_update"].includes(update.type);

    return isMatchingTrackingNumber && (isAllowedUpdateType || isSystemUpdate);
  });
}

function getExceptionPropsForTrackingItem(
  updatesForThisTrackingNumber: any,
  intl: IntlShape,
): TrackingExceptionProps | undefined {
  if (!updatesForThisTrackingNumber?.length) return;

  // CREATE EXCEPTION PROPS FOR PORTCAST
  if (updatesForThisTrackingNumber.every((update: any) => !!update?.additional_info?.container_metadata?.cntr_no)) {
    const latestUpdate = updatesForThisTrackingNumber.at(-1);
    const podScheduleArrivalFristSceen = latestUpdate.additional_info?.bill_of_lading?.pod_scheduled_arrival_lt_first_seen;
    const podScheduleArrivalFromSchedule =
      latestUpdate.additional_info?.bill_of_lading?.pod_scheduled_arrival_lt_from_schedule;
    if (
      podScheduleArrivalFristSceen &&
      podScheduleArrivalFromSchedule &&
      getDifferenceInDates(podScheduleArrivalFromSchedule, podScheduleArrivalFristSceen, "days") > 0
    ) {
      const scheduledLateDays = getDifferenceInDates(podScheduleArrivalFromSchedule, podScheduleArrivalFristSceen, "days");

      return {
        exceptionLabel: `Arriving at destination late by ${scheduledLateDays} day${scheduledLateDays > 1 ? "s" : ""}`,
        exceptionDetails: [
          {
            title: intl.formatMessage({
              id: "tracking.exception.previousETA",
              defaultMessage: "Previous ETA",
            }),
            description: moment(podScheduleArrivalFristSceen).utc().format(DateTimeFormat.fullDateTimeFormat),
          },
          {
            title: intl.formatMessage({
              id: "tracking.exception.updatedETA",
              defaultMessage: "Updated ETA",
            }),
            description: moment(podScheduleArrivalFromSchedule).utc().format(DateTimeFormat.fullDateTimeFormat),
          },
        ],
      };
    }
    if (latestUpdate?.additional_info?.delay_lists?.length > 0) {
      return {
        exceptionLabel: "Exception",
      };
    }
  } else {
    // CREATE EXCEPTION FOR OTHER UPDATES
    if (updatesForThisTrackingNumber.some((update: any) => update.tag === "Exception"))
      return {
        exceptionLabel: "Exception",
      };
    return undefined;
  }
}

function getPortcastExceptionDataForTrackingHistoryItem(latestUpdate: any) {
  const data: any = {};

  if (!latestUpdate?.additional_info?.delay_lists?.length) return;
  const delaysToShow = latestUpdate.additional_info.delay_lists.filter((delay: any) =>
    ["RLV", "LTD"].includes(delay.reason_code),
  );

  delaysToShow.forEach((delay: any) => {
    delay.schedule_change?.forEach((schedule: any) => {
      if (schedule.current_port === delay.port_code) {
        if (!data[`${schedule.current_port}_${schedule.event_type_code}`]) {
          data[`${schedule.current_port}_${schedule.event_type_code}`] = {
            exceptionReason: [delay.reason_code],
            previousEstimatedTime: schedule.old_date,
            updatedEstimatedTime: schedule.new_date,
            exceptionCreatedTime: delay.created,
          };
        } else {
          if (!data[`${schedule.current_port}_${schedule.event_type_code}`].exceptionReason.includes(delay.reason_code)) {
            data[`${schedule.current_port}_${schedule.event_type_code}`].exceptionReason.push(delay.reason_code);
          }
          if (
            moment(delay.created).isAfter(
              moment(data[`${schedule.current_port}_${schedule.event_type_code}`].exceptionCreatedTime),
            )
          ) {
            data[`${schedule.current_port}_${schedule.event_type_code}`].previousEstimatedTime = schedule.old_date;
            data[`${schedule.current_port}_${schedule.event_type_code}`].updatedEstimatedTime = schedule.new_date;
            data[`${schedule.current_port}_${schedule.event_type_code}`].exceptionCreatedTime = delay.created;
          }
        }
      }
    });
  });
  return data;
}

export { generateTrackingItems, getTrackingNumberOptions, refreshTrackingData, sortByTimestamp };

function stringifyPortcastAirEventType(eventType: string) {
  switch (eventType) {
    case "consignment_received":
      return "Consignment received";
    case "manifested_on_flight":
      return "Manifested on the flight";
    case "transferred_to_another_airline":
      return "Transferred to another airline";
    case "received_from_flight":
      return "Received from flight";
    case "consignee_notified_of_arrival":
      return "Consignee has been notified of the arrival";
    case "consignment_to_be_transferred_to_another_airline":
      return "Consignment to be transferred to another airline";
    case "delivered_to_consignee":
      return "Delivered to the consignee";
    case "prepared_for_loading_on_the_flight":
      return "Prepared for loading on the flight";
    case "freight_on_hand":
      return "Freight on hand";
    case "received_from_shipper":
      return "Received from shipper";
    case "departed":
      return "Departed";
    case "received_from_another_airline":
      return "Received from another airline";
    case "arrived_at_destination":
      return "Arrived at destination";
    case "documentation_delivered_to_consignee":
      return "Documentation has been delivered to the consignee";
    case "consignment_cleared_by_customs":
      return "Consignment cleared by Customs";
    case "discrepancy_details":
      return "Discrepancy details";
    case "documentation_received":
      return "Documentation has been received at this location";
    case "freight_Waybill_processed":
      return "Freight Waybill (FWB) processed";
    case "booked_for_transport":
      return "Booked for transport";
    default:
      return eventType;
  }
}

const stringifyStateAndCountry = (state?: string, country?: string) => `${state ?? ""}${state ? ", " : ""}${country}`;

export function stringifyExceptionSubtag(subtag: string) {
  switch (subtag) {
    case "Exception_001":
      return "Delivery of the package failed due to some shipping exception";
    case "Exception_002":
      return "Delivery of the package failed as the customer relocated";
    case "Exception_003":
      return "Delivery of the package failed as the recipient refused to take the package due to some reason";
    case "Exception_004":
      return "Delivery of the package failed as the recipient refused to take the package due to some reason";
    case "Exception_005":
      return "Package delayed due to some unforeseen reasons";
    case "Exception_006":
      return "The package being held due to pending payment from the customer's end";
    case "Exception_007":
      return "Package not delivered due to incorrect recipient address";
    case "Exception_008":
      return "Package available for the pickup but not collected by the customer";
    case "Exception_009":
      return "Package rejected by the carrier due to noncompliance with its guidelines";
    case "Exception_010":
      return "The package is on its way back to the sender";
    case "Exception_011":
      return "The return package has been successfully received by the sender";
    case "Exception_012":
      return "Shipment damaged";
    case "Exception_013":
      return "Delivery of the package failed as it got lost";
    default:
      return undefined;
  }
}

// https://cargobase.atlassian.net/browse/WEB-8920: To create extra updates when portcast doesn't send updates for certain destinations
const createExtraPortcastAirUpdates = (updates: any, intl: IntlShape) => {
  if (!updates || updates?.length === 0) return [];
  const routes = updates[0]?.routes;
  if (!routes || routes?.length === 0) return [];

  const destinations = routes?.map((route: any) => stringifyStateAndCountry(route.to?.state, route.to?.country));

  // If there are no updates for the destination, create extra updates
  const destinationsWithNoUpdates = destinations.filter(
    (destination: any) =>
      !updates.some((update: any) => {
        const { state, country } = update?.location ?? {};
        const location = stringifyStateAndCountry(state, country);
        return location === destination;
      }),
  );

  const extraUpdates = destinationsWithNoUpdates.map((destination: any) => {
    const destinationRouteInfo = routes.find((route: any) => {
      const location = `${route.to?.state}, ${route.to?.country}`;
      return location === destination;
    });
    const { state, country, name, iata_code } = destinationRouteInfo?.to ?? {};
    const isDestination = iata_code && iata_code === routes?.[routes.length - 1]?.to?.iata_code;

    return {
      date: destinationRouteInfo?.arrival_datetime?.estimated,
      isEstimatedDate: true,
      location: stringifyStateAndCountry(state, country),
      locationGroupSubtitle: `${name} (${iata_code})`,
      locationGroupBadgeText: isDestination
        ? intl.formatMessage({
            id: "tracking.destination",
            defaultMessage: "Destination",
          })
        : null,

      modeOfTransportationName: destinationRouteInfo?.flight_number || null,
      modeOfTransportation: "air",
    };
  });
  return extraUpdates;
};
